import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Phone, 
  Calendar, 
  Car, 
  ChevronDown,
  User,
  MapPin,
  Clock,
  Mail,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import Logo from '../Assets/MJ logo (1).png';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Detect scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isAuthModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isAuthModalOpen]);

  const navItems = [
    { path: '/', label: 'Home', icon: null },
    { path: '/about', label: 'About Us', icon: null },
    { 
      label: 'Services', 
      dropdown: [
        { path: '/vehicles', label: 'Car Rental' },
        { path: '/luxaryvehicles', label: 'Luxury Fleet' },
      ]
    },
    { path: '/tourpackages', label: 'Tour Packages', icon: <MapPin className="h-4 w-4 mr-2" /> },
    { path: '/blog', label: 'Blog', icon: null },
    { path: '/contact', label: 'Contact', icon: null },
  ];

  const contactInfo = {
    phone: '+91 97281 15781',
    hours: '24/7 Available'
  };

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleQuickBook = () => {
    closeMobileMenu();
    navigate('/booking-form');
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add your authentication logic here
    console.log('Form submitted:', formData);
    closeAuthModal();
  };

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white text-sm py-2">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center space-x-6 mb-2 sm:mb-0">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">{contactInfo.phone}</span>
              </div>
              <div className="hidden md:flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>{contactInfo.hours}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => openAuthModal('login')}
                className="flex items-center space-x-1 hover:text-blue-200 transition-colors"
              >
                <User className="h-4 w-4" />
                <span>Login / Register</span>
              </button>
              <button 
                onClick={handleQuickBook}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-4 py-1 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-200"
              >
                Get Instant Booking
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-2xl' : 'bg-white shadow-md'
      }`}>
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-3 z-50"
              onClick={closeMobileMenu}
            >
              <img 
                src={Logo} 
                alt="My Journey Cabs" 
                className="h-12 w-auto"
              />
              <div className="hidden lg:block">
                <h1 className="text-xl font-bold text-gray-800">My Journey Cab</h1>
                <p className="text-xs text-gray-500">Premium Travel Solutions</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <div key={item.label} className="relative group">
                  {item.dropdown ? (
                    <>
                      <button
                        className={`flex items-center px-4 py-3 font-medium text-sm transition-all duration-200 ${
                          item.dropdown.some(d => isActive(d.path))
                            ? 'text-blue-600'
                            : 'text-gray-700 hover:text-blue-600'
                        }`}
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                      >
                        {item.icon}
                        {item.label}
                        <ChevronDown className="h-4 w-4 ml-1" />
                      </button>
                      
                      {/* Services Dropdown */}
                      <div 
                        className={`absolute top-full left-0 w-64 bg-white shadow-2xl rounded-lg py-2 transition-all duration-300 ${
                          isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                        }`}
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                      >
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`flex items-center px-4 py-3 text-sm transition-colors ${
                              isActive(subItem.path)
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                          >
                            <Car className="h-4 w-4 mr-3 text-gray-400" />
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center px-4 py-3 font-medium text-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 shadow-md"
              >
                <Phone className="h-4 w-4" />
                <span className="font-semibold">Call Now</span>
              </a>
              <button
                onClick={() => navigate('/booking-form')}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-xl transition-all duration-200 font-semibold"
              >
                <Calendar className="h-4 w-4" />
                <span>Book Now</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors z-50"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-7 w-7" />
              ) : (
                <Menu className="h-7 w-7" />
              )}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-40 lg:hidden backdrop-blur-sm"
            onClick={closeMobileMenu}
          />
          
          <div className="fixed top-0 left-0 right-0 bottom-0 z-50 lg:hidden">
            <div className="absolute top-0 left-0 right-0 bottom-0 bg-white overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-4 py-4 flex items-center justify-between">
                <Link to="/" onClick={closeMobileMenu}>
                  <img 
                    src={Logo} 
                    alt="My Journey Cabs" 
                    className="h-10 w-auto"
                  />
                </Link>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-full bg-gray-100"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>

              <div className="px-4 py-6 space-y-1">
                {navItems.map((item) => (
                  <div key={item.label} className="border-b border-gray-100 last:border-0">
                    {item.dropdown ? (
                      <>
                        <div className="py-3 px-2 font-semibold text-gray-800 flex items-center">
                          {item.icon}
                          {item.label}
                        </div>
                        <div className="pl-6 pb-3 space-y-2">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={closeMobileMenu}
                              className={`block py-2 px-3 rounded-lg text-sm ${
                                isActive(subItem.path)
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center py-4 px-2 font-medium rounded-lg ${
                          isActive(item.path)
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-2xl">
                <div className="space-y-3">
                  <a
                    href={`tel:${contactInfo.phone}`}
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center space-x-2 w-full py-3 bg-green-500 text-white rounded-lg font-semibold"
                  >
                    <Phone className="h-5 w-5" />
                    <span>Call {contactInfo.phone}</span>
                  </a>
                  <button
                    onClick={handleQuickBook}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold shadow-lg"
                  >
                    Quick Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Login/Register Modal */}
  {/* Login/Register Modal */}
{/* Login/Register Modal */}
{isAuthModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    {/* Backdrop */}
    <div 
      className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
      onClick={closeAuthModal}
    />
    
    {/* Modal Content - Made smaller */}
    <div className={`relative bg-white rounded-lg shadow-xl w-full max-h-[80vh] overflow-y-auto ${
      authMode === 'login' ? 'max-w-xs' : 'max-w-sm'
    }`}>
      {/* Close Button - Smaller */}
      <button
        onClick={closeAuthModal}
        className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 transition-colors z-10"
      >
        <X className="h-4 w-4 text-gray-500" />
      </button>

      <div className="p-5">
        {/* Header - Compact */}
        <div className="text-center mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-2">
            <User className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-800">
            {authMode === 'login' ? 'Login' : 'Sign Up'}
          </h2>
          <p className="text-gray-500 text-xs mt-1">
            {authMode === 'login' 
              ? 'Access your account' 
              : 'Create new account'}
          </p>
        </div>

        {/* Form - Compact */}
        <form onSubmit={handleSubmit} className="space-y-2">
          {authMode === 'register' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Full name"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          {authMode === 'register' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="+91 98765 43210"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-8 pr-8 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>

          {authMode === 'register' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm password"
                  required
                />
              </div>
            </div>
          )}

          {authMode === 'login' && (
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3" />
                <span className="ml-1 text-gray-600">Remember me</span>
              </label>
              <a href="#" className="text-blue-600 hover:text-blue-700 font-medium text-xs">
                Forgot?
              </a>
            </div>
          )}

          <button
            type="submit"
            className="w-full py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded font-semibold text-xs hover:shadow-md transition-all duration-200"
          >
            {authMode === 'login' ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        {/* Toggle Mode - Compact */}
        <div className="mt-3 text-center">
          <p className="text-gray-600 text-xs">
            {authMode === 'login' ? "No account?" : 'Have account?'}
            <button
              onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className="ml-1 text-blue-600 hover:text-blue-700 font-semibold text-xs"
            >
              {authMode === 'login' ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </div>

        {/* Divider - Only show for signup */}
        {authMode === 'register' && (
          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div>
          </div>
        )}

        {/* Social Login - Only show for signup, smaller */}
        {authMode === 'register' && (
          <div className="flex space-x-2">
            <button className="flex-1 flex items-center justify-center p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-xs">
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </button>
            <button className="flex-1 flex items-center justify-center p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-xs">
              <svg className="h-4 w-4" fill="#1877F2" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
)}
    </>
  );
};

export default Header;